import type {BaseAreaChartProps, AreaChartProps} from '../types'
import {MarkColor} from '../types'
import {
  getDataWithDefaults,
  getTransformedPlotOptions,
  getXAxisOptionWithDefaults,
  getYAxisOptionsWithDefaults,
  isDev,
} from './shared'
import Chart from '../Chart'
import {CHART_CARD_CUSTOMIZATION_SETTINGS} from '../CustomizationDialog'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'

const MAX_NUMBER_OF_SERIES_ITEMS = 5

export const AreaDefaultColor = {
  green: MarkColor.green,
  teal: MarkColor.teal,
  blue: MarkColor.blue,
  indigo: MarkColor.indigo,
  orange: MarkColor.orange,
}

export const AreaDefaultFillColor = {
  green: '--display-green-scale-0',
  teal: '--display-teal-scale-0',
  blue: '--display-blue-scale-0',
  indigo: '--display-indigo-scale-0',
  orange: '--display-orange-scale-0',
}

export const BaseAreaChart = <T extends 'area' | 'areaspline'>({
  type,
  options,
  series: data,
  stacking = 'normal',
  marker,
  labels: initialLabelsValue,
  ...rest
}: BaseAreaChartProps<T>) => {
  const {xAxis, yAxis, plot} = options ?? {}

  const [hasDashStyle] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardHasLineStyle, true)
  const [userLabelsValue] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardLineDataLabels, undefined)
  const labels = userLabelsValue ?? initialLabelsValue
  const xyAxisOptionsTransformed = getXAxisOptionWithDefaults({xAxis, gridLineDashStyle: 'Solid'})

  const yAxisOptionsTransformed = getYAxisOptionsWithDefaults({yAxis, gridLineDashStyle: 'Solid'})

  const seriesWithColors = data?.map((s, index) => {
    const keys = Object.keys(AreaDefaultColor).filter(Boolean) as Array<keyof typeof AreaDefaultColor>
    const colorKey = keys[index % keys.length]
    return {
      ...s,
      color: s.color ? `var(${MarkColor[s.color]})` : colorKey ? `var(${AreaDefaultColor[colorKey]})` : undefined,
      fillColor: s.color ? undefined : colorKey ? `var(${AreaDefaultFillColor[colorKey]})` : undefined,
    }
  })

  const series = getDataWithDefaults({series: seriesWithColors as Highcharts.SeriesOptionsType[], type, hasDashStyle})

  const plotOptionsTransformed = getTransformedPlotOptions({plotOptions: plot, marker, labels, stacking})

  if (series.length > MAX_NUMBER_OF_SERIES_ITEMS && isDev()) {
    // eslint-disable-next-line no-console
    console.error('Ideally, an area chart should contain no more than 5 lines.')
  }

  return (
    <Chart
      type={type}
      xAxisOptions={xyAxisOptionsTransformed}
      yAxisOptions={yAxisOptionsTransformed}
      series={series as Highcharts.SeriesOptionsType[]}
      plotOptions={plotOptionsTransformed}
      {...rest}
    />
  )
}

const AreaChart = (props: AreaChartProps) => {
  return <BaseAreaChart {...props} type="area" />
}

export default AreaChart
